import { useQuery } from '@tanstack/react-query'
import { Entrada, Etiqueta, Girador, cn } from '@baiocchi/ui'
import { useState } from 'react'

import { chaves } from '../api/chaves'
import { leadsApi } from '../api/leads'
import { useDebounce } from '../ganchos/usar-debounce'
import { formatarTelefone } from '../utilitarios/formatar'
import { EtiquetaStatus } from './etiquetas-dominio'
import { IconeBusca, IconeX } from './icones'

export interface LeadEscolhido {
  uuid: string
  nome: string
  contato: string
}

/**
 * Busca de lead para vincular a um agendamento.
 *
 * Consulta `GET /leads?busca=`, que já compara telefone **por dígitos** — a coluna
 * guarda E.164, então o servidor normaliza e a tela não precisa saber disso.
 *
 * Só leads em situação não terminal são oferecidos: agendar visita para um
 * atendimento encerrado seria criar compromisso que a máquina de estados recusa.
 */
export function BuscadorLead({
  escolhido,
  onEscolher,
  id,
  temErro,
}: {
  escolhido: LeadEscolhido | null
  onEscolher: (lead: LeadEscolhido | null) => void
  id?: string
  temErro?: boolean
}) {
  const [termo, setTermo] = useState('')
  const busca = useDebounce(termo)

  const consulta = useQuery({
    queryKey: chaves.leads.lista({ busca, limite: 8, ocultarFinalizados: 'true' }),
    queryFn: ({ signal }) =>
      leadsApi.listar({ busca, limite: 8, ocultarFinalizados: 'true' }, signal),
    enabled: busca.trim().length >= 2 && escolhido === null,
    staleTime: 30_000,
  })

  if (escolhido) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-borda-forte bg-superficie-2 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-conteudo">{escolhido.nome}</p>
          <p className="truncate text-xs tabular-nums text-conteudo-fraco">
            {formatarTelefone(escolhido.contato)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            onEscolher(null)
            setTermo('')
          }}
          aria-label="Trocar lead"
          className="shrink-0 rounded p-1 text-conteudo-fraco hover:bg-superficie-3 hover:text-conteudo"
        >
          <IconeX className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <Entrada
        id={id}
        value={termo}
        temErro={temErro}
        placeholder="Buscar lead por nome ou telefone…"
        prefixo={
          consulta.isFetching ? <Girador tamanho="sm" rotulo={null} /> : <IconeBusca className="h-4 w-4" />
        }
        onChange={(evento) => setTermo(evento.target.value)}
      />

      {busca.trim().length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-borda bg-superficie p-1 shadow-lg">
          {consulta.isPending ? (
            <p className="px-3 py-3 text-center text-xs text-conteudo-fraco">Buscando…</p>
          ) : (consulta.data?.dados.length ?? 0) === 0 ? (
            <p className="px-3 py-3 text-center text-xs text-conteudo-fraco">
              Nenhum lead ativo encontrado.
            </p>
          ) : (
            <ul>
              {consulta.data?.dados.map((lead) => (
                <li key={lead.uuid}>
                  <button
                    type="button"
                    onClick={() =>
                      onEscolher({
                        uuid: lead.uuid,
                        nome: lead.cliente.nome,
                        contato: lead.cliente.contatoPrincipal,
                      })
                    }
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded px-2.5 py-2 text-left',
                      'hover:bg-superficie-2',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-conteudo">
                        {lead.cliente.nome}
                      </span>
                      <span className="block truncate text-xs tabular-nums text-conteudo-fraco">
                        {formatarTelefone(lead.cliente.contatoPrincipal)}
                        {lead.responsavel ? ` · ${lead.responsavel.nome}` : ' · sem responsável'}
                      </span>
                    </span>
                    <span className="shrink-0">
                      <EtiquetaStatus status={lead.status} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {escolhido === null && termo.length > 0 && termo.trim().length < 2 && (
        <Etiqueta tom="neutro" className="mt-1.5">
          Digite ao menos 2 caracteres
        </Etiqueta>
      )}
    </div>
  )
}
